import * as React from "react";

import { ChevronDown, Pin, Plus, X } from "lucide-react";

import { cn } from "../../utils";
import { BadgeCitation, BadgeCitationKey } from "./badge-citation";
import { Button } from "./button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

interface EvidenceCardCitation {
  id: string;
  fileName: string;
  page: string;
  citationKey: string;
  sourceText?: string;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

type EvidenceCardRootProps = React.ComponentProps<"article"> & {
  pinned?: boolean;
  size?: "md" | "sm";
  expanded?: boolean;
  onToggleExpand?: () => void;
  onAdd?: () => void;
  canAdd?: boolean;
};

type EvidenceCardComponent = React.ForwardRefExoticComponent<
  React.PropsWithoutRef<EvidenceCardRootProps> &
    React.RefAttributes<HTMLElement>
> & {
  Header: typeof EvidenceCardHeader;
  Title: typeof EvidenceCardTitle;
  PinButton: typeof EvidenceCardPinButton;
  Note: typeof EvidenceCardNote;
  Citations: typeof EvidenceCardCitations;
  Citation: typeof EvidenceCardCitationBlock;
  CitationName: typeof EvidenceCardCitationName;
};

const EvidenceCardRoot = React.forwardRef<
  HTMLDivElement,
  EvidenceCardRootProps
>(
  (
    {
      className,
      pinned,
      size = "md",
      expanded = false,
      onToggleExpand,
      onAdd,
      canAdd = true,
      children,
      ...props
    },
    ref,
  ) => {
    if (size === "sm") {
      const childArray = React.Children.toArray(children);
      const hasExpandableContent = childArray.some(
        (child) =>
          React.isValidElement<React.ComponentProps<"p">>(child) &&
          child.type === EvidenceCardNote &&
          React.Children.count(child.props.children) > 0,
      );
      const staticChildren = childArray.filter(
        (child) =>
          !(React.isValidElement(child) && child.type === EvidenceCardNote),
      );
      const expandableChildren = childArray.filter(
        (child) =>
          React.isValidElement(child) && child.type === EvidenceCardNote,
      );

      return (
        <EvidenceCardVariantContext.Provider
          value={{
            size,
            onToggleExpand: hasExpandableContent ? onToggleExpand : undefined,
          }}
        >
          <div
            ref={ref}
            className={cn(
              "af-group af-flex af-flex-col af-rounded-md af-px-2 af-py-1.5 af-transition-colors hover:af-bg-interface",
              className,
            )}
          >
            <div className="af-flex af-items-start af-justify-between af-gap-2">
              <div className="af-flex af-min-w-0 af-flex-1 af-items-start af-gap-1">
                {hasExpandableContent ? (
                  <button
                    type="button"
                    className="af-mt-0.5 af-flex af-shrink-0 af-items-center af-justify-center af-p-0.5"
                    onClick={onToggleExpand}
                  >
                    <ChevronDown
                      className={cn(
                        "af-size-3 af-text-muted-foreground af-transition-transform",
                        !expanded && "af--rotate-90",
                      )}
                    />
                  </button>
                ) : (
                  <span className="af-w-4" />
                )}
                <div className="af-min-w-0 af-flex-1">
                  {staticChildren}
                  {hasExpandableContent && (
                    <div
                      className={cn(
                        "transition-[grid-template-rows] af-grid",
                        expanded ? "af-grid-rows-[1fr]" : "af-grid-rows-[0fr]",
                      )}
                    >
                      <div className="af-overflow-hidden">
                        {expandableChildren}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {onAdd && (
                <Button
                  type="button"
                  size="icon-xs"
                  variant="ghost"
                  aria-label="Add evidence"
                  className="af-shrink-0"
                  disabled={!canAdd}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAdd();
                  }}
                >
                  <Plus className="af-size-3" />
                </Button>
              )}
            </div>
          </div>
        </EvidenceCardVariantContext.Provider>
      );
    }

    return (
      <article
        ref={ref}
        className={cn(
          "af-flex af-flex-col af-gap-3 af-rounded-lg af-border af-border-interface-border af-bg-interface af-p-4 af-transition-colors hover:af-bg-secondary/40",
          pinned && "af-border-transparent af-bg-secondary/20",
          className,
        )}
        {...props}
      >
        {children}
      </article>
    );
  },
);
EvidenceCardRoot.displayName = "EvidenceCard";

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function EvidenceCardHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "af-flex af-items-center af-justify-between af-gap-3",
        className,
      )}
      {...props}
    />
  );
}
EvidenceCardHeader.displayName = "EvidenceCard.Header";

// ---------------------------------------------------------------------------
// Title
// ---------------------------------------------------------------------------

function EvidenceCardTitle({ className, ...props }: React.ComponentProps<"p">) {
  const { size, onToggleExpand } = React.useContext(EvidenceCardVariantContext);
  const isSmall = size === "sm";

  return (
    <p
      role={isSmall && onToggleExpand ? "button" : undefined}
      tabIndex={isSmall && onToggleExpand ? 0 : undefined}
      className={cn(
        isSmall
          ? "af-min-w-0 af-truncate af-text-sm af-font-medium af-leading-5 af-text-foreground"
          : "af-min-w-0 af-flex-1 af-truncate af-text-sm af-font-medium af-leading-none af-text-foreground",
        isSmall && onToggleExpand && "af-cursor-pointer",
        className,
      )}
      onClick={isSmall ? onToggleExpand : undefined}
      onKeyDown={
        isSmall && onToggleExpand
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggleExpand();
              }
            }
          : undefined
      }
      {...props}
    />
  );
}
EvidenceCardTitle.displayName = "EvidenceCard.Title";

// ---------------------------------------------------------------------------
// Variant context — shared disabled + size state
// ---------------------------------------------------------------------------

const EvidenceCardFieldContext = React.createContext<{
  disabled?: boolean;
}>({});

const EvidenceCardVariantContext = React.createContext<{
  size: "md" | "sm";
  onToggleExpand?: () => void;
}>({ size: "md" });

// ---------------------------------------------------------------------------
// PinButton
// ---------------------------------------------------------------------------

function EvidenceCardPinButton({
  className,
  pinned,
  onClick,
  ...props
}: React.ComponentProps<typeof Button> & { pinned?: boolean }) {
  const fieldContext = React.useContext(EvidenceCardFieldContext);
  const isDisabled = fieldContext.disabled === true || props.disabled === true;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-pressed={pinned}
      aria-label={pinned ? "Unpin evidence" : "Pin evidence"}
      className={cn(
        "af-text-muted-foreground hover:af-text-foreground",
        pinned && "af-text-primary hover:af-text-primary",
        className,
      )}
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      disabled={isDisabled}
    >
      <Pin className={cn("af-size-3", pinned && "af-fill-current")} />
    </Button>
  );
}
EvidenceCardPinButton.displayName = "EvidenceCard.PinButton";

// ---------------------------------------------------------------------------
// Note
// ---------------------------------------------------------------------------

function EvidenceCardNote({ className, ...props }: React.ComponentProps<"p">) {
  const { size } = React.useContext(EvidenceCardVariantContext);

  if (size === "sm") {
    return (
      <p
        className={cn(
          "af-mt-0.5 af-line-clamp-5 af-min-w-0 af-whitespace-normal af-break-words af-text-sm af-leading-relaxed af-text-muted-foreground",
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <p
      className={cn(
        "af-line-clamp-5 af-min-w-0 af-whitespace-normal af-break-words af-text-sm af-leading-5 af-text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}
EvidenceCardNote.displayName = "EvidenceCard.Note";

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

const groupCitationsByFile = (citations: EvidenceCardCitation[]) => {
  const byFile = new Map<string, EvidenceCardCitation[]>();
  for (const citation of citations) {
    const existing = byFile.get(citation.fileName) ?? [];
    existing.push(citation);
    byFile.set(citation.fileName, existing);
  }
  return Array.from(byFile.entries());
};

function EvidenceCardCitations({
  citations,
  className,
  children,
  onClick,
  onCitationClick,
  onRemoveCitation,
  highlightedCitationId,
  ...props
}: React.ComponentProps<"div"> & {
  citations?: EvidenceCardCitation[];
  children?: React.ReactNode;
  onCitationClick?: (citation: EvidenceCardCitation) => void;
  onRemoveCitation?: (citation: EvidenceCardCitation) => void;
  highlightedCitationId?: string;
}) {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onClick?.(e);
  };

  if (citations) {
    const grouped = groupCitationsByFile(citations);
    const citationNumberById = new Map(
      citations.map((citation, index) => [citation.id, String(index + 1)]),
    );
    if (grouped.length === 0) return null;
    return (
      <div
        className={cn("af-flex af-flex-col af-gap-0.5", className)}
        onClick={handleClick}
        {...props}
      >
        <TooltipProvider>
          {grouped.map(([fileName, fileCitations]) => (
            <div
              key={fileName}
              className="af-flex af-flex-wrap af-items-center af-gap-1"
            >
              <span className="af-text-xs af-font-medium af-text-muted-foreground">
                {fileName}
              </span>
              {fileCitations.map((citation) => {
                const isHighlighted = highlightedCitationId === citation.id;
                const badge = (
                  <BadgeCitation
                    role={onCitationClick ? "button" : undefined}
                    tabIndex={onCitationClick ? 0 : undefined}
                    onClick={() => onCitationClick?.(citation)}
                    onKeyDown={(event) => {
                      if (!onCitationClick) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onCitationClick(citation);
                      }
                    }}
                    className={cn(
                      onCitationClick && "af-cursor-pointer",
                      isHighlighted && "af-ring-2 af-ring-primary",
                    )}
                  >
                    <BadgeCitationKey>
                      {citationNumberById.get(citation.id)}
                    </BadgeCitationKey>
                    p. {citation.page}
                    {onRemoveCitation && (
                      <button
                        type="button"
                        aria-label="Remove citation"
                        className="af--mr-1 af-inline-flex af-size-4 af-items-center af-justify-center af-rounded-sm af-text-muted-foreground hover:af-bg-muted hover:af-text-foreground focus-visible:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-ring"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemoveCitation(citation);
                        }}
                      >
                        <X className="af-size-3" />
                      </button>
                    )}
                  </BadgeCitation>
                );

                if (!citation.sourceText) {
                  return (
                    <React.Fragment key={citation.id}>{badge}</React.Fragment>
                  );
                }

                return (
                  <Tooltip key={citation.id}>
                    <TooltipTrigger asChild>{badge}</TooltipTrigger>
                    <TooltipContent
                      className="af-max-w-80 af-text-xs"
                      side="top"
                    >
                      {citation.sourceText}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </TooltipProvider>
      </div>
    );
  }

  if (!children) return null;

  return (
    <div
      className={cn("af-flex af-flex-col af-gap-0.5", className)}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  );
}
EvidenceCardCitations.displayName = "EvidenceCard.Citations";

// New composable subcomponents for a more layout-driven API
function EvidenceCardCitationBlock({
  children,
  className,
  ...props
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn("af-flex af-items-center af-gap-1", className)}
      {...props}
    >
      {children}
    </div>
  );
}
EvidenceCardCitationBlock.displayName = "EvidenceCard.Citation";

function EvidenceCardCitationName({
  children,
  className,
  ...props
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <span
      className={cn(
        "af-text-xs af-font-medium af-text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}
EvidenceCardCitationName.displayName = "EvidenceCard.CitationName";

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

const EvidenceCard = EvidenceCardRoot as EvidenceCardComponent;

EvidenceCard.Header = EvidenceCardHeader;
EvidenceCard.Title = EvidenceCardTitle;
EvidenceCard.PinButton = EvidenceCardPinButton;
EvidenceCard.Note = EvidenceCardNote;
EvidenceCard.Citations = EvidenceCardCitations;
EvidenceCard.Citation = EvidenceCardCitationBlock;
EvidenceCard.CitationName = EvidenceCardCitationName;

export { EvidenceCard, EvidenceCardFieldContext };
export type { EvidenceCardCitation };
export { EvidenceCardCitationBlock, EvidenceCardCitationName };
